import moment from "moment";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery } from "react-query";
import {
  findRE,
  getFinancialNestedCategories,
} from "../../../../repositories/qerpapi/erp/financeiro/ResultadoExercicioRepository";
import { findCompaniesV3 } from "../../../../repositories/qerpapi/v3/EmpresaRepository";
import { notify } from "../../../../utils/notify";
import {
  orientationLabelOptions,
  orientationOptions,
  periods,
  reportTypes,
} from "../../constants";
import {
  consolidatedColumns,
  detailedColumns,
  reInitialFilters,
} from "../ResultadoExercicioConstant";

const ResultadoExercicioContext = createContext();

const ResultadoExercicioProvider = ({ children }) => {
  const [filters, setFilters] = useState({ ...reInitialFilters });
  const [financeCategoriesFilter, setFinanceCategoriesFilter] = useState({
    filtered: [],
    grouped: [],
  });

  const categoriesCheckedIds = useMemo(() => [], []);

  const parentsIds = useMemo(() => ({}), []);

  const [filterPeriod, setFilterPeriod] = useState({
    ...reInitialFilters.searchDate,
  });

  const [isMainFetchEnabled, setMainFetchEnable] = useState(false);

  const [isConsolidated, setConsolidated] = useState(true);

  const [isGroupedByFinanceCategory, setIsGroupedByFinanceCategory] =
    useState(true);
  const [isDrawerOpen, setDrawerOpen] = useState(false);

  const [columns, setColumns] = useState(consolidatedColumns);

  const [cachedCompanies, setCachedCompanies] = useState({});
  const [cachedFinanceCategories, setCachedFinanceCategories] = useState([]);

  const [cachedMovementData, setCachedMovementData] = useState([]);
  const [cachedPeriodTotal, setCachedPeriodTotal] = useState();
  const [cachedPeriodResult, setCachedPeriodResult] = useState({});

  const [orientation, setOrientation] = useState(orientationOptions.Retrato);
  const [orientationLabel, setOrientationLabel] = useState(
    orientationLabelOptions.Retrato
  );

  // ----------------------------------------------------------------------------

  const setSelectedCompany = useCallback((newValue) => {
    setFilters((prev) => ({
      ...prev,
      multicompany: newValue,
    }));
  }, []);

  const setReportType = useCallback((newValue) => {
    if (newValue === reportTypes.Detalhado) {
      setOrientation(orientationOptions.Paisagem);
      setOrientationLabel(orientationLabelOptions.Paisagem);
    } else {
      setOrientation(orientationOptions.Retrato);
      setOrientationLabel(orientationLabelOptions.Retrato);
    }
    setFilters((prev) => ({
      ...prev,
      reportType: newValue,
    }));
  }, []);

  const setCustomDatePeriod = useCallback((newValue) => {
    setFilterPeriod((prev) => {
      return {
        ...prev,
        ...newValue,
      };
    });
  }, []);

  const setPeriod = useCallback(
    (newValue) => {
      if (newValue === periods.Personalizado) {
        setCustomDatePeriod({
          startDate: undefined,
          endDate: undefined,
        });
      }

      setFilters((prev) => ({
        ...prev,
        period: newValue,
      }));
    },
    [setCustomDatePeriod]
  );

  const setSearchDate = useCallback((newValue) => {
    setFilters((prev) => ({
      ...prev,
      startDate: newValue?.startDate
        ? moment(newValue?.startDate).format("YYYY-MM-DD")
        : moment().startOf("month").format("YYYY-MM-DD"),
      endDate: newValue?.endDate
        ? moment(newValue?.endDate).format("YYYY-MM-DD")
        : moment().endOf("month").format("YYYY-MM-DD"),
    }));
  }, []);

  const setAccountingSystems = useCallback((newValue) => {
    setFilters((prev) => ({
      ...prev,
      accountingSystem: newValue,
    }));
  }, []);

  const setShowNotPayedAccounts = useCallback((newValue) => {
    setFilters((prev) => ({
      ...prev,
      showNotPayedAccounts: newValue,
    }));
  }, []);

  const setShowNotReceivedTitles = useCallback((newValue) => {
    setFilters((prev) => ({
      ...prev,
      showNotReceivedTitles: newValue,
    }));
  }, []);

  const setCategorizedReleases = useCallback((newValue) => {
    setFilters((prev) => ({
      ...prev,
      categorizedRelease: newValue,
    }));
  }, []);

  const setGroupByFinanceCategory = useCallback((newValue) => {
    setFilters((prev) => ({
      ...prev,
      groupByFinanceCategory: newValue,
    }));
  }, []);

  const setFinanceFilter = useCallback(
    (newValue) => {
      setFinanceCategoriesFilter((prev) => ({
        ...prev,
        ...newValue,
      }));

      const filteredFinanceCategoriesString = categoriesCheckedIds.join(",");
      setFilters((prev) => ({
        ...prev,
        financeCategories: filteredFinanceCategoriesString,
      }));
    },
    [categoriesCheckedIds]
  );

  // ----------------------------------------------------------------------------

  const getFilteredCategories = useCallback(
    (categories) => {
      return categories
        .filter((category) => category.checked)
        .map((filteredCategory) => {
          categoriesCheckedIds.push(filteredCategory.id);
          return {
            ...filteredCategory,
            children: getFilteredCategories(filteredCategory.children),
          };
        });
    },
    [categoriesCheckedIds]
  );

  const applyFinanceFilter = useCallback(
    (financeCategories = cachedFinanceCategories) => {
      categoriesCheckedIds.length = 0;
      const filtered = getFilteredCategories(financeCategories);
      const grouped = financeCategories.filter((item) => item.checked);

      setFinanceFilter({
        filtered,
        grouped,
      });
    },
    [
      cachedFinanceCategories,
      getFilteredCategories,
      categoriesCheckedIds,
      setFinanceFilter,
    ]
  );

  const getOptionsToList = (objectOption) => {
    const options = Object.keys(objectOption).map((item) => ({
      description: item,
      value: objectOption[item],
    }));
    return options;
  };

  // ----------------------------------------------------------------------------

  const applyPeriod = useCallback(
    (periodDates) => {
      setSearchDate(periodDates);
      setCachedPeriodResult(periodDates);
      setCustomDatePeriod({
        startDate: periodDates?.startDate
          ? moment(periodDates.startDate, "YYYY-MM-DD").toDate()
          : moment().startOf("month").toDate(),
        endDate: periodDates?.endDate
          ? moment(periodDates.endDate, "YYYY-MM-DD").toDate()
          : moment().endOf("month").toDate(),
      });
    },
    [setCustomDatePeriod, setSearchDate]
  );

  useEffect(() => {
    setSearchDate(filterPeriod);
  }, [filterPeriod, setSearchDate]);

  // ----------------------------------------------------------------------------

  const recursiveCheckChildren = useCallback((category, check) => {
    return {
      ...category,
      children: category.children.map((child) =>
        recursiveCheckChildren(child, check)
      ),
      checked: check,
    };
  }, []);

  const findAndCheckChildren = useCallback(
    ({ categories, checkedCategory, check }) => {
      return categories.map((category) => {
        if (
          category.id === checkedCategory.id &&
          category.businessCenterId === checkedCategory.businessCenterId
        ) {
          category = recursiveCheckChildren(category, check);
        } else {
          if (category.children.length)
            category.children = findAndCheckChildren({
              categories: category.children,
              checkedCategory,
              check,
            });
        }
        return category;
      });
    },
    [recursiveCheckChildren]
  );

  const findParents = useCallback(
    (categories, checkedCategory, check, checkedTree) => {
      const searchedCategories = categories.map((category) => {
        if (
          category.id === checkedCategory.parentId &&
          !parentsIds[category.businessCenterId ?? category.id]?.includes(
            category.id
          )
        ) {
          parentsIds[category.businessCenterId ?? category.id] = !parentsIds[
            category.businessCenterId ?? category.id
          ]
            ? [category.id]
            : [
                ...parentsIds[category.businessCenterId ?? category.id],
                category.id,
              ];
          findParents(checkedTree, category, check, checkedTree);
        } else {
          if (category.children.length)
            category.children = findParents(
              category.children,
              checkedCategory,
              check,
              checkedTree
            );
        }

        return category;
      });

      return searchedCategories;
    },
    [parentsIds]
  );

  const recursiveCheckParents = useCallback((categories, parents, check) => {
    return categories.map((category) => {
      const parentId = category.businessCenterId ?? category.id;
      const parent = parents[parentId];

      if (parent?.includes(category.id)) {
        const isAllUnchecked = category.children?.every(
          (child) => !child.checked
        );

        if (isAllUnchecked && !check) {
          const parentIndex = parent.indexOf(category.id);
          parent.splice(parentIndex, 1);
        }

        category = {
          ...category,
          children: recursiveCheckParents(category.children, parents, check),
          checked: isAllUnchecked ? check : true,
        };
      }

      return category;
    });
  }, []);

  const handleCheck = useCallback(
    (checkedCategory, check) => {
      const categoriesWithChildrenChecked = findAndCheckChildren({
        categories: cachedFinanceCategories,
        checkedCategory,
        check,
      });

      const checkedTree = categoriesWithChildrenChecked.filter(
        (category) => category.id === checkedCategory.businessCenterId
      );

      findParents(checkedTree, checkedCategory, check, checkedTree);

      const categoriesWithParentsChecked = recursiveCheckParents(
        categoriesWithChildrenChecked,
        parentsIds,
        check
      );

      setCachedFinanceCategories(categoriesWithParentsChecked);
      return categoriesWithParentsChecked;
    },
    [
      cachedFinanceCategories,
      findAndCheckChildren,
      findParents,
      parentsIds,
      recursiveCheckParents,
    ]
  );

  const clearAllFinanceFilter = useCallback(
    (categories) => {
      setIsGroupedByFinanceCategory(true);
      setGroupByFinanceCategory(true);
      for (const key in parentsIds) {
        delete parentsIds[key];
      }
      const uncheckedCategories = categories.map((category) =>
        recursiveCheckChildren(category, false)
      );
      setCachedFinanceCategories(uncheckedCategories);
    },
    [parentsIds, recursiveCheckChildren, setGroupByFinanceCategory]
  );
  // ----------------------------------------------------------------------------

  const {
    isLoading: reLoading,
    refetch: reRefetch,
    isRefetching: reRefetchLoading,
  } = useQuery("resultado-exercicio", async () => await findRE(filters), {
    enabled: !!sessionStorage.getItem("gfAccessToken") && isMainFetchEnabled,
    onSuccess: async (res) => await handleREData(res),
  });

  const refetch = useCallback(async () => {
    const consolidated = filters.reportType === reportTypes.Consolidado;

    const columnsLayout = {
      consolidated: consolidatedColumns,
      detailed: detailedColumns,
    };
    await reRefetch();

    setIsGroupedByFinanceCategory(filters.groupByFinanceCategory);
    setConsolidated(consolidated);
    setColumns(columnsLayout[filters.reportType]);
  }, [filters.groupByFinanceCategory, filters.reportType, reRefetch]);

  const handleREData = useCallback(
    async (res) => {
      const data = res?.data;

      if (!res?.success) {
        if (res.statusCode === 401) {
          notify("Token expirado", true, "error");
        }
        notify(res.message, true, "error");
      }
      applyPeriod(data?.searchPeriod);
      setCachedMovementData(data?.movements || []);
      setCachedPeriodTotal(data?.total);
    },
    [applyPeriod]
  );

  // ----------------------------------------------------------------------------

  const { isLoading: loadingCompanies } = useQuery(
    "companies",
    async () => await findCompaniesV3(),
    {
      enabled: !!sessionStorage.getItem("gfAccessToken"),
      onSuccess: (companies) => handleCompaniesData(companies),
    }
  );

  const validateIfCompanyIdExistAndSetNewDefault = useCallback(
    async (userCompanies) => {
      const companiesIds = Object.values(userCompanies);

      if (!companiesIds.includes(filters.multicompany)) {
        setSelectedCompany(userCompanies.Todos);
      }
    },
    [filters.multicompany, setSelectedCompany]
  );

  const handleCompaniesData = useCallback(
    async (companies) => {
      const data = companies?.data ?? [];

      if (!companies.success) notify(companies.message, true, "error");

      if (companies?.data) {
        const allCompaniesString = data.map((company) => company.id).join(",");
        const formatedCompanies = {
          Todos: allCompaniesString,
        };

        data.forEach(
          (company) => (formatedCompanies[company.nomeempresa] = company.id)
        );

        await validateIfCompanyIdExistAndSetNewDefault(formatedCompanies);

        setCachedCompanies(formatedCompanies);
        setMainFetchEnable(true);
      }
    },
    [validateIfCompanyIdExistAndSetNewDefault]
  );

  // ----------------------------------------------------------------------------

  const { isLoading: loadingCategories } = useQuery(
    "categories",
    async () =>
      await getFinancialNestedCategories(
        sessionStorage.getItem("empresa_cnpj")
      ),
    {
      enabled: !!sessionStorage.getItem("empresa_cnpj"),
      onSuccess: (categories) => handleCategoriesData(categories),
    }
  );

  const createRecursiveCategoriesTree = useCallback(
    (data, businessCenterId) => {
      return data
        .filter((category) => category.ativo === "S")
        .map((category) => {
          if (category.pivot?.idcentronegocio) {
            businessCenterId = category.pivot?.idcentronegocio;
          }

          return {
            id: category.id,
            desc: !!category.codigo
              ? `${category.codigo} - ${category.descricao}`
              : category.descricao,
            checked: false,
            parentId:
              category.centrocustopai_id ?? category.pivot?.idcentronegocio,
            businessCenterId,
            children: createRecursiveCategoriesTree(
              category.categorias_financeiras ?? [],
              businessCenterId
            ),
          };
        });
    },
    []
  );

  const handleCategoriesData = useCallback(
    (categories) => {
      const data = categories?.data ?? [];

      if (!categories.success) notify(categories.message, true, "error");

      if (data.length) {
        const activeCategories = createRecursiveCategoriesTree(data);

        setCachedFinanceCategories(activeCategories);
      }
    },
    [createRecursiveCategoriesTree]
  );

  // ----------------------------------------------------------------------------

  const contextValue = useMemo(
    () => ({
      filters,
      setFilters,
      filterPeriod,
      setFilterPeriod,
      financeCategoriesFilter,
      setCachedFinanceCategories,

      isConsolidated,
      setConsolidated,

      columns,
      setColumns,

      isGroupedByFinanceCategory,
      isDrawerOpen,
      setDrawerOpen,

      reLoading,
      refetch,
      reRefetchLoading,

      cachedMovementData,
      cachedPeriodResult,
      cachedPeriodTotal,
      cachedFinanceCategories,
      cachedCompanies,

      orientation,
      setOrientation,
      orientationLabel,
      setOrientationLabel,

      loadingCompanies,

      setSelectedCompany,
      setReportType,
      setPeriod,
      setSearchDate,
      setAccountingSystems,
      setShowNotPayedAccounts,
      setShowNotReceivedTitles,
      setCategorizedReleases,
      setFinanceFilter,
      setCustomDatePeriod,
      setGroupByFinanceCategory,
      setIsGroupedByFinanceCategory,

      applyFinanceFilter,
      getOptionsToList,

      handleCheck,
      clearAllFinanceFilter,

      loadingCategories,
    }),
    [
      filters,
      filterPeriod,
      financeCategoriesFilter,
      isConsolidated,
      columns,
      isGroupedByFinanceCategory,
      isDrawerOpen,
      reLoading,
      refetch,
      reRefetchLoading,
      cachedMovementData,
      cachedPeriodResult,
      cachedPeriodTotal,
      cachedFinanceCategories,
      cachedCompanies,
      orientation,
      setOrientation,
      orientationLabel,
      setOrientationLabel,
      loadingCompanies,
      setSelectedCompany,
      setReportType,
      setPeriod,
      setSearchDate,
      setAccountingSystems,
      setShowNotPayedAccounts,
      setShowNotReceivedTitles,
      setCategorizedReleases,
      setFinanceFilter,
      setCustomDatePeriod,
      setGroupByFinanceCategory,
      applyFinanceFilter,
      handleCheck,
      clearAllFinanceFilter,
      loadingCategories,
    ]
  );

  return (
    <ResultadoExercicioContext.Provider value={contextValue}>
      {children}
    </ResultadoExercicioContext.Provider>
  );
};

const useResultadoExercicioService = () => {
  const context = useContext(ResultadoExercicioContext);

  if (!context) {
    throw new Error(
      "useResultadoExercicioService must be used with a ResultadoExercicioProvider"
    );
  }

  return context;
};

export { useResultadoExercicioService, ResultadoExercicioProvider };
